/**
 * Utility functions for error handling
 */

export interface ErrorWithMessage {
  message: string
  code?: string
  status?: number
}

/**
 * Type guard to check if error has a message property
 */
function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  )
}

/**
 * Safely extract error message from unknown error
 */
export function getErrorMessage(error: unknown): string {
  if (isErrorWithMessage(error)) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return 'An unknown error occurred'
}

/**
 * Safely extract error code from unknown error
 */
export function getErrorCode(error: unknown): string | null {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as Record<string, unknown>).code
    return typeof code === 'string' ? code : null
  }
  return null
}

/**
 * Check if error requires authentication
 */
export function isAuthError(error: unknown): boolean {
  if (isErrorWithMessage(error)) {
    return error.code === 'AUTH_REQUIRED' || error.status === 401
  }
  return false
}
